import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { NotificationContext } from "./NotificationContext";
import { applyLiveNotification } from "../api/applyLiveNotification";
import { notificationQueryKeys } from "../api/query-keys";
import { useUnreadNotificationCount } from "../api/useUnreadNotificationCount";
import { notificationSchema } from "../schemas";

import { realtimeClient } from "@/features/realtime";
import { useAuth } from "@/lib/hooks/useAuth";

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();

  useEffect(() => {
    if (!userId) return;

    let joined = false;

    const channel = realtimeClient
      .channel(`user:${userId}`)
      .on({ event: "*" }, (frame) => {
        const parsed = notificationSchema.safeParse(frame);
        if (!parsed.success) {
          console.error("[notifications] invalid realtime frame:", parsed.error);
          return;
        }
        applyLiveNotification(queryClient, parsed.data);
      })
      .subscribe((status) => {
        if (status !== "SUBSCRIBED") return;
        // A re-join means the socket was down, and the hub does not replay what it sent
        // while we were gone — only a refetch can close that gap.
        if (joined) void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
        joined = true;
      });

    return () => {
      void channel.remove();
    };
  }, [userId, queryClient]);

  const value = useMemo(() => ({ unreadCount }), [unreadCount]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
